import { NoDataComponent, CustomButton } from '@/shared/ui/components';
import { notificationIcon } from '@/shared/ui/assets';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { Badge, Button, Drawer, Popover, Spin } from 'antd';
import _ from 'lodash';
import React from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { t } from 'ttag';
import { notificationDateFormatter } from '../../lib/notificationCenter';
import {
  noMoreNotificationsRenderer,
  renderNotificationItem,
  skeletonRender,
} from './notificationRenderers';
import { useNotificationsCenter } from './useNotificationsCenter';
import type { NotificationItem } from '../../lib/notificationCenter/notificationCenter.types';
import './notificationCenter.scss';

/**
 * Sidebar notification bell with its unseen badge, reminder popover and the notification drawer. Reads state and callbacks from useNotificationsCenter, shows the count in a Badge behind a dismissible popover prompt, and renders a left Drawer whose body groups the notification list by date, falls back to a skeleton while loading or a no-data message when empty, and loads more on bottom scroll with a spinner and end-of-list footer.
 *
 * @returns The notification center element.
 */
const NotificationsCenter = () => {
  const {
    state,
    notiState,
    setNotiState,
    couldViewFlaggedStudies,
    notificationList,
    callbackFuncs,
    onClickNotification,
    toogleDrawer,
    useBottomScrollListener,
  } = useNotificationsCenter();

  /**
   * Renders the notification drawer header carrying the Notifications title. Adds a danger-styled view-flagged-studies button wired to onClickNotification only when couldViewFlaggedStudies is true.
   *
   * @returns The notification drawer header element.
   */
  const notiHeader = () => (
    <div className="notification-header-container">
      <strong className="header">{t`Notifications`}</strong>
      {couldViewFlaggedStudies && (
        <CustomButton
          id="view-flagged-studies-btn"
          className="view-flagged-button"
          onClick={() => onClickNotification()}
          style="danger"
          type="tertiary"
        >
          {t`View flagged studies`}
        </CustomButton>
      )}
    </div>
  );

  const popoverContent = (
    <div className="popover-content-wrapper">
      <div>
        Some updates require your action! Check notifications to ensure smooth clinic workflow.
      </div>
      <CustomButton
        size="small"
        style="neutral"
        type="tertiary"
        className="close-popover-btn"
        onClick={() => {
          setNotiState({ openPopover: false });
        }}
        ariaLabel="Close notification content popover"
      >
        <CloseOutlined />
      </CustomButton>
    </div>
  );

  return (
    <div className="notification-icon-container">
      <Popover
        arrow={{ pointAtCenter: true }}
        open={notiState.openPopover}
        content={popoverContent}
        placement="right"
        rootClassName="notification-popover"
      >
        <Badge className="notification-badge-count" count={notiState.count}>
          <Button
            className="notification-btn"
            aria-label={t`Notifications`}
            onClick={() => {
              if (notiState.openPopover) {
                setNotiState({ openPopover: false });
              }
              toogleDrawer();
            }}
          >
            <img src={notificationIcon} alt="notificationIcon" aria-hidden="true" />
          </Button>
        </Badge>
      </Popover>
      <Drawer
        rootClassName="root-noti-class"
        aria-label={t`Notifications`}
        placement="left"
        onClose={toogleDrawer}
        open={state.visibleNoti}
        zIndex={100}
        title={notiHeader()}
        closeIcon={false}
        width={500}
        destroyOnHidden
      >
        <BottomScrollListener offset={50} onBottom={useBottomScrollListener}>
          {(scrolRef) => (
            <div
              ref={scrolRef as React.Ref<HTMLDivElement>}
              id="notification-body-container"
              className="body-container"
            >
              {state.isLoadedData ? (
                notificationList.length > 0 ? (
                  _.map(
                    notificationList,
                    (notificationBlock) =>
                      notificationBlock.data.length > 0 && (
                        <div className="notification-item-container">
                          <div className="date">
                            {notificationDateFormatter(notificationBlock.date)}
                          </div>
                          <div className="list">
                            {_.map(notificationBlock.data, (notificationItem) =>
                              renderNotificationItem(
                                notificationItem,
                                callbackFuncs,
                                couldViewFlaggedStudies,
                              ),
                            )}
                          </div>
                        </div>
                      ),
                  )
                ) : (
                  <NoDataComponent message="No notifications yet!" />
                )
              ) : (
                skeletonRender()
              )}
              <div className="loading-more">
                {state.isLoadingMore && (
                  <>
                    <Spin indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />} />
                    <div className="loading-more__text">{t`Loading more...`}</div>
                  </>
                )}
                {noMoreNotificationsRenderer({
                  notificationList: notificationList as NotificationItem[],
                  isLoadingMore: state.isLoadingMore,
                  isLoadedData: state.isLoadedData,
                  isEndOfData: state.isEndOfData,
                })}
              </div>
            </div>
          )}
        </BottomScrollListener>
      </Drawer>
    </div>
  );
};

export default NotificationsCenter;
